import React, { useState } from 'react';
import { Clock, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass = "w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/40 outline-none transition-colors [color-scheme:dark]";

const EmployeeScheduleEditor = ({ employee, sessionId, onClose, onSaved }) => {
  const [schedule, setSchedule] = useState({
    scheduled_start: employee.scheduled_start || '09:00',
    scheduled_end: employee.scheduled_end || '17:00',
    grace_period_minutes: employee.grace_period_minutes || 5,
    weekly_overtime_limit_hours: employee.weekly_overtime_limit_hours || null,
    overtime_week_start_day: employee.overtime_week_start_day || 'Monday'
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API}/manager/employee/${employee.id}/schedule`,
        schedule,
        { params: { session_id: sessionId } }
      );

      if (response.data.success) {
        toast.success('Schedule updated successfully');
        onSaved();
        onClose();
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update schedule');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/15 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Edit Schedule</h2>
              <p className="text-sm text-zinc-400">{employee.first_name} {employee.last_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="schedule-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Scheduled Start Time
            </label>
            <input
              type="time"
              value={schedule.scheduled_start}
              onChange={(e) => setSchedule({...schedule, scheduled_start: e.target.value})}
              className={inputClass}
              data-testid="schedule-start-time"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Scheduled End Time
            </label>
            <input
              type="time"
              value={schedule.scheduled_end}
              onChange={(e) => setSchedule({...schedule, scheduled_end: e.target.value})}
              className={inputClass}
              data-testid="schedule-end-time"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Grace Period (minutes after start time)
            </label>
            <input
              type="number"
              min="0"
              max="60"
              value={schedule.grace_period_minutes}
              onChange={(e) => setSchedule({...schedule, grace_period_minutes: parseInt(e.target.value) || 0})}
              className={inputClass}
              data-testid="schedule-grace-period"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Employee can check in up to {schedule.grace_period_minutes} minutes late without being marked late
            </p>
          </div>

          <div className="pt-4 border-t border-zinc-800">
            <h3 className="text-sm font-semibold text-white mb-3">Weekly Overtime Limits</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Maximum Weekly Overtime (hours)
              </label>
              <input
                type="number"
                min="0"
                max="40"
                step="0.5"
                value={schedule.weekly_overtime_limit_hours || ''}
                onChange={(e) => setSchedule({...schedule, weekly_overtime_limit_hours: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="No limit"
                className={inputClass}
                data-testid="schedule-overtime-limit"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Leave empty for no limit. When reached, overtime requests will be blocked.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Overtime Week Starts On
              </label>
              <select
                value={schedule.overtime_week_start_day}
                onChange={(e) => setSchedule({...schedule, overtime_week_start_day: e.target.value})}
                className={inputClass}
                data-testid="schedule-week-start"
              >
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
              </select>
              <p className="text-xs text-zinc-500 mt-1">
                Day when the weekly overtime counter resets
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            data-testid="schedule-save-btn"
            className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-50 text-emerald-200 ring-1 ring-emerald-400/25 font-semibold py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl hover:bg-zinc-700 transition-colors text-zinc-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeScheduleEditor;
